import type { DeclarationReadModel } from "../../../../../api/types"
import type { StatementCard } from "../useStudioV2State"

/**
 * REFERENZLISTE, PER VERORDNUNG — the read-model join behind the unit card's
 * "Referenzliste" row (the piece v1 carried as a per-statement select in
 * `StudioScreenA` and V2 initially dropped: the reviewer could SEE and SET
 * which edition of e.g. the REACH candidate list a statement is read
 * against; without it, a declaration citing an edition our catalogue does
 * not hold was a dead end — the statement stayed open with nothing to do).
 *
 * Three facts per framework, all straight from the read model (which is
 * `skipCaseConversion` — raw snake_case, same as every other read here):
 *
 * - `options` — the CMS catalogue (`edition_catalogs`, newest first), with
 *   any statement's own `listVersion` UNIONED IN even when the catalogue no
 *   longer lists it (v1's own rule: a value the run produced must stay
 *   pickable/visible, or the current state would be unrepresentable).
 * - `readAgainst` — the edition the run reads this framework against, BY
 *   NAME where the catalogue holds the id, the raw id otherwise (an honest
 *   answer beats a blank).
 * - `citesUnknownByGroup` — group index → the edition name the DOCUMENT
 *   cited that our catalogue does not hold ("Stand 04.02.2026"). The third
 *   reference-list case is a fact about OUR content; quoting the document's
 *   own wording keeps it distinguishable from "named nothing".
 * - `chosen` — the axis's own confirmation record (`editions[fw].chosen`,
 *   picking IS confirming). Half of `unknownEditionResolved`'s input
 *   (`studio/cellState.ts`, twinned with the backend's `unknown_edition_resolved`
 *   gate): the citation warning must close the moment the FRAMEWORK-level
 *   ambiguity is confirmed away, not only once a per-statement `listVersion`
 *   lands too.
 */
export interface FrameworkEditionInfo {
  options: { id: string; name: string; published?: string | null }[]
  readAgainst: string | null
  readAgainstId: string | null
  citesUnknownByGroup: Map<number, string>
  chosen: string | null
}

export function editionInfoByFramework(
  readModel: DeclarationReadModel | null,
  cardsByFramework: ReadonlyMap<string, StatementCard[]>,
): Map<string, FrameworkEditionInfo> {
  const out = new Map<string, FrameworkEditionInfo>()
  const catalogs = readModel?.edition_catalogs ?? {}
  const editions = readModel?.editions ?? {}

  const frameworks = new Set<string>([
    ...Object.keys(catalogs),
    ...Object.keys(editions),
    ...cardsByFramework.keys(),
  ])

  for (const fw of frameworks) {
    let options = (catalogs[fw] ?? []).map((e) => ({
      id: e.id,
      name: e.name,
      published: e.published,
    }))
    for (const card of cardsByFramework.get(fw) ?? []) {
      const lv = card.listVersion
      if (lv && !options.some((o) => o.id === lv))
        options.push({ id: lv, name: lv, published: null })
    }

    const axis = editions[fw]
    const selected = axis?.selected ?? null
    if (selected) {
      options = [
        ...options.filter((option) => option.id === selected),
        ...options.filter((option) => option.id !== selected),
      ]
    }
    const readAgainst = selected
      ? (options.find((o) => o.id === selected)?.name ?? selected)
      : null

    const citesUnknownByGroup = new Map<number, string>()
    for (const u of axis?.cites_unknown_edition ?? []) {
      if (u.named) citesUnknownByGroup.set(u.group_index, u.named)
    }

    // A framework with NO catalogue, NO selection and NO citation renders no
    // Referenzliste row at all — the row must never be noise on frameworks
    // that simply have no edition axis (RoHS exemptions etc.).
    if (options.length || readAgainst || citesUnknownByGroup.size) {
      out.set(fw, {
        options,
        readAgainst,
        readAgainstId: selected,
        citesUnknownByGroup,
        chosen: axis?.chosen ?? null,
      })
    }
  }
  return out
}
